import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Checkbox, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserData from '../../../assets/data/UserData';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import { ControlpanelErrorButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog, StyledDataGrid } from '../../../components/StyledComponents';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import CustomGridToolbarQuickFilter from '../CustomGridToolbarQuickFilter';
import CustomPagination from '../CustomPagination';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';

const CustomToolbar = () => {
   return (
      <GridToolbarContainer className='flex justify-between'>
         <div>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
         </div>
         <CustomGridToolbarQuickFilter />
      </GridToolbarContainer>
   );
};

const DeleteMultipleUsersDialog = ({ onClose }) => {
   const { t } = useTranslation();
   const [rowData, setRowData] = useState(UserData());
   const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);

   const handleCheckboxChange = useCallback(
      (employeeCode, columnName, isChecked) => {
         let updatedRows = rowData.map((rowData) => {
            if (rowData.employeeCode === employeeCode) {
               return { ...rowData, [columnName]: isChecked };
            }
            return rowData;
         });
         setRowData(updatedRows);
      },
      [rowData]
   );
   const columnData = useMemo(
      () => [
         { field: 'employeeCode', headerName: t('common.employee-code'), minWidth: 150, flex: 1 },
         { field: 'firstName', headerName: t('common.first-name'), minWidth: 150, flex: 1 },
         { field: 'lastName', headerName: t('common.last-name'), minWidth: 150, flex: 1 },
         { field: 'email', headerName: t('button.email'), minWidth: 150, flex: 1 },
         {
            field: 'isSelected',
            headerName: t('common.content-077'),
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
               <Checkbox
                  checked={params.row.isAdmin}
                  onChange={(event) => {
                     handleCheckboxChange(params.row.employeeCode, 'isAdmin', event.target.checked);
                  }}
               />
            ),
         },
      ],
      [t, handleCheckboxChange]
   );

   return (
      <CustomDialog maxWidth={'xl'} open onClose={onClose} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('button.delete-multiple-users')}</p>
               </div>
               <GrayIconButton onClick={onClose} className='self-start'>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <p className='pt-5'>{t('common.content-078')}</p>
            <div className='max-h-[400px] mt-5'>
               <StyledDataGrid
                  rows={rowData}
                  columns={columnData}
                  getRowId={(v) => v.employeeCode}
                  slots={{
                     toolbar: CustomToolbar,
                     pagination: CustomPagination,
                     loadingOverlay: LinearProgress,
                     noRowsOverlay: NoRowOverlay,
                     columnsPanel: CustomColumnsPanel,
                  }}
                  pageSizeOptions={[5, 10, 25]}
                  disableRowSelectionOnClick
                  localeText={tableTranslated}
               />
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={onClose}>
                  <CloseIcon />
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelErrorButton>
                  <CheckIcon />
                  <p>{t('button.delete')}</p>
               </ControlpanelErrorButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default DeleteMultipleUsersDialog;
